import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Employee } from '../../../models/employee';
import { Office } from '../../../models/office';
import { Collect } from '../../../models/collect';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import useAxiosInterceptor from '../../AxiosInterceptor';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
    stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';

import { useTheme, useMediaQuery, Button, Box, Card, CardContent, Tooltip, Modal, Paper, Stack, Chip, Typography, Divider, IconButton, Grid, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, TextField } from '@mui/material';
import {
    Add as AddIcon,
    AssignmentInd as AssignmentIndIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PhoneAndroid as PhoneAndroidIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';

// 👇 importa tu tabla simple y tipos
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
    iconActionColumnSizing,
    simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn } from '../../../components/table/interface';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { isSuperAdminUser } from '../../../utils/authScope';

type RowId = string | number; // 👈 acepta string o number
interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

type MobileDevice = {
    id: number;
    installation_id: string;
    status: string;
    device_name?: string | null;
    platform?: string | null;
    brand?: string | null;
    model?: string | null;
    app_version?: string | null;
    last_login_at?: string | null;
    last_seen_at?: string | null;
};

type MobileDeviceRequest = {
    id: number;
    installation_id: string;
    status: string;
    device_name?: string | null;
    platform?: string | null;
    app_version?: string | null;
    request_reason?: string | null;
    review_note?: string | null;
    requested_at?: string | null;
    reviewed_at?: string | null;
};

const mobileModalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: 900,
    height: '85vh',
    maxHeight: 720,
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 2,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
    overflow: 'hidden',
};

const passwordModalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: 480,
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 2,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
};

const assignModalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: 560,
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 2,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
};

const Empleados: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = 'Empleados - Mis Rutas';
    useAxiosInterceptor();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [mobileModalOpen, setMobileModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [mobileDevices, setMobileDevices] = useState<MobileDevice[]>([]);
    const [mobileDeviceRequests, setMobileDeviceRequests] = useState<MobileDeviceRequest[]>([]);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignEmployee, setAssignEmployee] = useState<Employee | null>(null);
    const [assignOfficeId, setAssignOfficeId] = useState<string>('');
    const [assignCollectId, setAssignCollectId] = useState<string>('');
    const [assignCollects, setAssignCollects] = useState<Collect[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
    const [officeId, setOfficeId] = useState<string>('');
    const [collectId, setCollectId] = useState<string>('');
    const [showUnassigned, setShowUnassigned] = useState(false);
    const [officesLoaded, setOfficesLoaded] = useState(false);
    const { drawerOpen } = useDrawer();
    const {
        canCreate,
        canUpdate,
        canDelete,
        canManageMobile,
        canAssign,
        authUserData,
        isLoadingPermissions,
    } = useResourcePermissions('/administrar/empleados');
    const isSuperAdmin = useMemo(() => isSuperAdminUser(authUserData), [authUserData]);

    const [selectedIds, setSelectedIds] = useState<RowId[]>([]); // si quieres usar selección

    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordTarget, setPasswordTarget] = useState<{
        id: number;
        fullName: string;
        username: string;
    } | null>(null);
    const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
    const [passwordLoading, setPasswordLoading] = useState(false);

    const loadEmployees = async (selectedOfficeId?: string, selectedCollectId?: string) => {
        const { data } = await axios.get(`employees`, {
            params: {
                ...(selectedOfficeId ? { office_id: selectedOfficeId } : {}),
                ...(selectedCollectId ? { collect_id: selectedCollectId } : {}),
            },
        });
        setEmployees(Array.isArray(data?.data) ? data.data : []);
    };

    useEffect(() => {
        if (isLoadingPermissions || !officesLoaded) {
            return;
        }

        if (!isSuperAdmin && offices.length > 0 && !officeId) {
            return;
        }

        const fetchEmployeesByOffice = async () => {
            showLoading();
            try {
                await loadEmployees(officeId, collectId);
                await new Promise((resolve) => setTimeout(resolve, 500));
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                console.error(error);
                showAlert('Error al obtener los empleados.', 'error');
            } finally {
                hideLoading();
            }
        };

        void fetchEmployeesByOffice();
    }, [collectId, officeId, isSuperAdmin, isLoadingPermissions, offices.length, officesLoaded]);

    useEffect(() => {
        if (isLoadingPermissions) {
            return;
        }

        const fetchOffices = async () => {
            try {
                const officesUrl = isSuperAdmin ? 'alloffices' : 'offices';
                const { data } = await axios.get(officesUrl);
                const options = Array.isArray(data?.data) ? data.data : [];
                setOffices(options);
                if (options.length === 0) {
                    setOfficeId('');
                    setCollectId('');
                    return;
                }

                if (isSuperAdmin) {
                    setOfficeId('');
                    setCollectId('');
                    return;
                }

                const currentOfficeAllowed = options.some(
                    (office: Office) => String(office.id) === String(officeId)
                );

                if (!currentOfficeAllowed) {
                    setOfficeId(String(options[0].id));
                    setCollectId('');
                }
            } catch (error) {
                console.error(error);
                setOffices([]);
                setOfficeId('');
                setCollectId('');
            } finally {
                setOfficesLoaded(true);
            }
        };

        void fetchOffices();
    }, [isSuperAdmin, isLoadingPermissions]);

    useEffect(() => {
        const fetchCollectsByOffice = async () => {
            if (!officeId) {
                setCollectsOffice([]);
                setCollectId('');
                return;
            }

            try {
                const { data } = await axios.get(`collectsoffice/${officeId}`);
                const collects = Array.isArray(data) ? data : [];
                setCollectsOffice(collects);
                if (isSuperAdmin) {
                    if (collectId && !collects.some((collect: Collect) => String(collect.id) === String(collectId))) {
                        setCollectId('');
                    }
                    return;
                }

                if (collects.length === 0) {
                    setCollectId('');
                    return;
                }

                const currentCollectAllowed = collects.some(
                    (collect: Collect) => String(collect.id) === String(collectId)
                );
                if (!currentCollectAllowed) {
                    setCollectId(String(collects[0].id));
                }
            } catch (error) {
                console.error(error);
                setCollectsOffice([]);
                setCollectId('');
            }
        };

        fetchCollectsByOffice();
    }, [collectId, isSuperAdmin, officeId]);

    const loadEmployeeMobileData = async (employeeId: number) => {
        const [devicesResponse, requestsResponse] = await Promise.all([
            axios.get(`employees/${employeeId}/mobile-devices`),
            axios.get(`employees/${employeeId}/mobile-device-requests`),
        ]);

        setMobileDevices(Array.isArray(devicesResponse.data) ? devicesResponse.data : []);
        setMobileDeviceRequests(Array.isArray(requestsResponse.data) ? requestsResponse.data : []);
    };

    const handleOpenMobileModal = async (employeeId: RowId) => {
        if (!canManageMobile) return;

        const numericId = typeof employeeId === 'string' ? Number(employeeId) : employeeId;
        if (!Number.isFinite(numericId)) {
            showAlert('ID inválido', 'error');
            return;
        }

        const employee = employees.find((item) => item.id === numericId) || null;
        setSelectedEmployee(employee);
        setMobileModalOpen(true);

        try {
            showLoading();
            await loadEmployeeMobileData(numericId);
        } catch (error: any) {
            console.error(error);
            showAlert('No fue posible cargar la gestión mobile del empleado.', 'error');
        } finally {
            hideLoading();
        }
    };

    const handleCloseMobileModal = () => {
        setMobileModalOpen(false);
        setSelectedEmployee(null);
        setMobileDevices([]);
        setMobileDeviceRequests([]);
    };

    const getAssignedCollectIds = (excludeEmployeeId?: number) => {
        const assigned = new Set<number>();

        employees.forEach((employee) => {
            if (excludeEmployeeId && Number(employee.id) === Number(excludeEmployeeId)) {
                return;
            }

            getEmployeeCollects(employee).forEach((collect: any) => {
                const collectIdValue = Number(collect?.id);
                if (Number.isFinite(collectIdValue)) {
                    assigned.add(collectIdValue);
                }
            });
        });

        return assigned;
    };

    const loadAssignableCollects = async (officeValue: string, employee: Employee) => {
        if (!officeValue) {
            setAssignCollects([]);
            setAssignCollectId('');
            return;
        }

        const currentCollectIds = new Set(
            getEmployeeCollects(employee)
                .map((collect: any) => Number(collect?.id))
                .filter((id: number) => Number.isFinite(id))
        );
        const assignedCollectIds = getAssignedCollectIds(employee.id);
        const { data } = await axios.get(`collectsoffice/${officeValue}`);
        const availableCollects = (Array.isArray(data) ? data : []).filter((collect: Collect) => {
            const collectIdValue = Number(collect?.id);
            if (!Number.isFinite(collectIdValue)) {
                return false;
            }

            if (currentCollectIds.has(collectIdValue)) {
                return true;
            }

            const directAssignedUserId = Number((collect as any)?.user?.id ?? 0);
            if (directAssignedUserId > 0 && directAssignedUserId !== Number(employee.id)) {
                return false;
            }

            return !assignedCollectIds.has(collectIdValue);
        });

        setAssignCollects(availableCollects);

        const currentCollectId = Array.from(currentCollectIds).find((collectIdValue) =>
            availableCollects.some((collect) => Number(collect.id) === Number(collectIdValue))
        );

        setAssignCollectId(
            currentCollectId
                ? String(currentCollectId)
                : availableCollects.length > 0
                    ? String(availableCollects[0].id)
                    : ''
        );
    };

    const handleOpenAssignModal = async (employeeId: RowId) => {
        if (!canAssign) return;

        const numericId = typeof employeeId === 'string' ? Number(employeeId) : employeeId;
        if (!Number.isFinite(numericId)) {
            showAlert('ID inválido', 'error');
            return;
        }

        const employee = employees.find((item) => Number(item.id) === Number(numericId)) || null;
        if (!employee) {
            showAlert('Empleado no encontrado', 'error');
            return;
        }

        if (offices.length === 0) {
            showAlert('No hay oficinas disponibles para asignar', 'warning');
            return;
        }

        const employeeOffice = getEmployeeOffices(employee)[0];
        const preferredOfficeId = offices.some((office) => Number(office.id) === Number(employeeOffice?.id))
            ? String(employeeOffice?.id)
            : String(offices[0].id);

        setAssignEmployee(employee);
        setAssignModalOpen(true);
        setAssignOfficeId(preferredOfficeId);

        try {
            showLoading();
            await loadAssignableCollects(preferredOfficeId, employee);
        } catch (error) {
            console.error(error);
            setAssignCollects([]);
            setAssignCollectId('');
            showAlert('No fue posible cargar los cobros disponibles.', 'error');
        } finally {
            hideLoading();
        }
    };

    const handleCloseAssignModal = () => {
        setAssignModalOpen(false);
        setAssignEmployee(null);
        setAssignOfficeId('');
        setAssignCollectId('');
        setAssignCollects([]);
    };

    const handleAssignOfficeChange = async (nextOfficeId: string) => {
        setAssignOfficeId(nextOfficeId);

        if (!assignEmployee) {
            setAssignCollects([]);
            setAssignCollectId('');
            return;
        }

        try {
            showLoading();
            await loadAssignableCollects(nextOfficeId, assignEmployee);
        } catch (error) {
            console.error(error);
            setAssignCollects([]);
            setAssignCollectId('');
            showAlert('No fue posible cargar los cobros disponibles.', 'error');
        } finally {
            hideLoading();
        }
    };

    const handleSaveAssignedCollect = async () => {
        if (!canAssign || !assignEmployee?.id) return;

        if (!assignOfficeId || !assignCollectId) {
            showAlert('Selecciona una oficina y un cobro para continuar.', 'warning');
            return;
        }

        try {
            showLoading();
            const { data } = await axios.post(`employees/${assignEmployee.id}/assign-collect`, {
                office_id: Number(assignOfficeId),
                collect_id: Number(assignCollectId),
            });

            setEmployees((prev) =>
                prev.map((employee) =>
                    Number(employee.id) === Number(assignEmployee.id)
                        ? (data?.data as Employee)
                        : employee
                )
            );
            showAlert(data?.message || 'Cobro asignado correctamente.', 'success');
            handleCloseAssignModal();
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || 'No fue posible asignar el cobro.',
                'error'
            );
        } finally {
            hideLoading();
        }
    };

    const handleMobileDeviceStatusChange = async (
        deviceId: number,
        status: 'approved' | 'blocked'
    ) => {
        if (!canManageMobile) return;
        if (!selectedEmployee?.id) return;

        try {
            showLoading();
            const { data } = await axios.put(`mobile-devices/${deviceId}/status`, {
                status,
            });
            await loadEmployeeMobileData(selectedEmployee.id);
            showAlert(data.message || 'Estado del dispositivo actualizado.', 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || 'No fue posible actualizar el estado del dispositivo.',
                'error'
            );
        } finally {
            hideLoading();
        }
    };

    const handleMobileDeviceRequestStatusChange = async (
        requestId: number,
        status: 'approved' | 'rejected'
    ) => {
        if (!canManageMobile) return;
        if (!selectedEmployee?.id) return;

        try {
            showLoading();
            const { data } = await axios.put(`mobile-device-requests/${requestId}/status`, {
                status,
            });
            await loadEmployeeMobileData(selectedEmployee.id);
            showAlert(data.message || 'Solicitud actualizada correctamente.', 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || 'No fue posible actualizar la solicitud.',
                'error'
            );
        } finally {
            hideLoading();
        }
    };

    const handleOpenPasswordModal = (row: { id: RowId; fullName: string; username: string }) => {
        if (!isSuperAdmin) return;
        const numericId = typeof row.id === 'string' ? Number(row.id) : row.id;
        if (!Number.isFinite(numericId)) {
            showAlert('ID inválido', 'error');
            return;
        }
        setPasswordTarget({
            id: numericId,
            fullName: row.fullName,
            username: row.username,
        });
        setGeneratedPassword(null);
        setPasswordModalOpen(true);
    };

    const handleClosePasswordModal = () => {
        setPasswordModalOpen(false);
        setPasswordTarget(null);
        setGeneratedPassword(null);
        setPasswordLoading(false);
    };

    const handleGenerateEmployeePassword = async () => {
        if (!passwordTarget?.id) return;
        setPasswordLoading(true);
        setGeneratedPassword(null);
        try {
            const { data } = await axios.post(
                `employees/${passwordTarget.id}/regenerate-password-superadmin`,
            );
            const plain = data?.data?.new_password ?? '';
            setGeneratedPassword(plain);
            showAlert(data?.message || 'Contraseña generada.', 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(
                error?.response?.data?.message || 'No fue posible generar la contraseña.',
                'error',
            );
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleCopyGeneratedPassword = async () => {
        if (!generatedPassword) return;
        try {
            await navigator.clipboard.writeText(generatedPassword);
            showAlert('Contraseña copiada al portapapeles', 'info');
        } catch {
            showAlert('No se pudo copiar al portapapeles', 'warning');
        }
    };

    // ✅ ahora acepta RowId y convierte a number internamente
    const handleDeleteEmployee = async (id: RowId) => {
        if (!canDelete) return;

        const numericId = typeof id === 'string' ? Number(id) : id;
        if (!Number.isFinite(numericId)) {
            showAlert('ID inválido', 'error');
            return;
        }

        const confirmDelete = window.confirm('¿Estás seguro de que deseas eliminar este empleado?');
        if (!confirmDelete) return;

        try {
            showLoading();
            await axios.delete(`employees/${numericId}`);
            setEmployees((prev) => prev.filter((e) => e.id !== numericId));
            setSelectedIds((prev) => prev.filter((x) => x !== numericId));
            showAlert('Empleado eliminado correctamente', 'success');
        } catch (error: any) {
            console.error(error);
            showAlert(error?.response?.data?.message || 'Error al eliminar el empleado', 'error');
        } finally {
            hideLoading();
        }
    };

    // 👇 columnas para SimpleCustomTable
    const simpleColumns: SimpleColumn[] = [
        { field: 'idnumber', headerName: 'Cédula', minWidth: 120, maxWidth: 120 },
        { field: 'fullName', headerName: 'Nombres y Apellidos', minWidth: 180, maxWidth: 180 },
        { field: 'username', headerName: 'Usuario', minWidth: 120, maxWidth: 120 },
        { field: 'cellphone', headerName: 'Celular', minWidth: 120, maxWidth: 120 },
        { field: 'office', headerName: 'Oficina', minWidth: 100, maxWidth: 100 },
        { field: 'collect', headerName: 'Cobro', minWidth: 100, maxWidth: 100 },
        { field: 'mobileSessionStatus', headerName: 'Sesión Mobile', minWidth: 140, maxWidth: 140 },

        ...(isSuperAdmin
            ? [
                  {
                      field: 'opt_view_password',
                      headerName: '',
                      ...iconActionColumnSizing(isMobile),
                      renderCell: ({ row }: { row: Record<string, unknown> }) => (
                          <Tooltip title="Generar nueva contraseña (solo superadmin)">
                              <span>
                                  <Button
                                      type="button"
                                      variant="contained"
                                      color="warning"
                                      sx={simpleTableIconButtonSx}
                                      aria-label="Ver o generar contraseña"
                                      onClick={() =>
                                          handleOpenPasswordModal({
                                              id: row.id as RowId,
                                              fullName: String(row.fullName ?? ''),
                                              username: String(row.username ?? ''),
                                          })
                                      }
                                  >
                                      <VisibilityIcon fontSize="small" />
                                  </Button>
                              </span>
                          </Tooltip>
                      ),
                  } as SimpleColumn,
              ]
            : []),

        // ✳️ opción EDITAR
        {
            field: 'opt_assign',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) =>
                row.isUnassigned ? (
                    <PermissionButton
                        allowed={canAssign}
                        deniedTooltip="Sin permiso para asignar cobro"
                        tooltip="Asignar cobro"
                        variant="contained"
                        color="info"
                        sx={simpleTableIconButtonSx}
                        onClick={() => handleOpenAssignModal(row.id)}
                    >
                        <AssignmentIndIcon fontSize="small" />
                    </PermissionButton>
                ) : null,
        },
        {
            field: 'opt_mobile',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canManageMobile}
                    deniedTooltip="Sin permiso para gestionar mobile"
                    tooltip="Gestionar mobile"
                    variant="contained"
                    color="secondary"
                    sx={simpleTableIconButtonSx}
                    onClick={() => handleOpenMobileModal(row.id)}
                >
                    <PhoneAndroidIcon fontSize="small" />
                </PermissionButton>
            ),
        },
        {
            field: 'opt_edit',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canUpdate}
                    deniedTooltip="Sin permiso para editar"
                    tooltip="Editar"
                    variant="contained"
                    color="primary"
                    sx={simpleTableIconButtonSx}
                    component={NavLink}
                    to={`/administrar/empleados/${row.id}/editar`}
                >
                    <EditIcon fontSize="small" />
                </PermissionButton>
            ),
        },

        // ✳️ opción ELIMINAR (como tu ejemplo)
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            renderCell: ({ row }) => (
                <PermissionButton
                    allowed={canDelete}
                    deniedTooltip="Sin permiso para eliminar"
                    tooltip="Eliminar"
                    variant="contained"
                    color="error"
                    sx={simpleTableIconButtonSx}
                    onClick={() => handleDeleteEmployee(row.id)}
                >
                    <DeleteIcon fontSize="small" />
                </PermissionButton>
            ),
        },
    ];

    const getEmployeeCollects = (employee: Employee) => {
        const directCollects = Array.isArray(employee.collects) ? employee.collects : [];
        if (directCollects.length > 0) {
            return directCollects;
        }

        const userCollects = Array.isArray((employee as any).userCollect)
            ? (employee as any).userCollect
            : [];

        return userCollects
            .map((item: any) => item?.collect)
            .filter((collect: any) => Boolean(collect?.id));
    };

    const getEmployeeOffices = (employee: Employee) => {
        const collectOffices = getEmployeeCollects(employee)
            .map((collect: any) => collect?.office)
            .filter((office: any) => Boolean(office?.id));

        if (collectOffices.length > 0) {
            return collectOffices;
        }

        const userOffices = Array.isArray((employee as any).userOffices)
            ? (employee as any).userOffices
            : [];

        return userOffices
            .map((item: any) => item?.office)
            .filter((office: any) => Boolean(office?.id));
    };

    const isEmployeeUnassigned = (employee: Employee) =>
        getEmployeeCollects(employee).length === 0 && getEmployeeOffices(employee).length === 0;

    // 👇 filas planas para la tabla simple
    const filteredEmployees = employees.filter((employee) => {
        const employeeCollects = getEmployeeCollects(employee);
        const employeeOffices = getEmployeeOffices(employee);
        const isUnassigned = isEmployeeUnassigned(employee);

        if (isSuperAdmin && showUnassigned) {
            return isUnassigned;
        }

        if (isSuperAdmin && !officeId) {
            if (collectId) {
                return employeeCollects.some((collect: any) => String(collect?.id) === String(collectId));
            }
            return true;
        }

        if (!officeId) return false;
        if (collectId) {
            return employeeCollects.some((collect: any) => String(collect?.id) === String(collectId));
        }
        return employeeOffices.some((office: any) => String(office?.id) === String(officeId));
    });

    const employeesForTable = isSuperAdmin
        ? [...filteredEmployees].sort((a, b) => {
            const oa =
                getEmployeeOffices(a)[0]?.name ??
                (isEmployeeUnassigned(a) ? 'Sin oficina' : '');
            const ob =
                getEmployeeOffices(b)[0]?.name ??
                (isEmployeeUnassigned(b) ? 'Sin oficina' : '');
            const byOffice = String(oa).localeCompare(String(ob), 'es', { sensitivity: 'base' });
            if (byOffice !== 0) return byOffice;
            const ca =
                getEmployeeCollects(a)[0]?.name ??
                (isEmployeeUnassigned(a) ? 'Sin cobro' : '');
            const cb =
                getEmployeeCollects(b)[0]?.name ??
                (isEmployeeUnassigned(b) ? 'Sin cobro' : '');
            return String(ca).localeCompare(String(cb), 'es', { sensitivity: 'base' });
        })
        : filteredEmployees;

    const rows = employeesForTable.map((employee) => {
        const employeeCollects = getEmployeeCollects(employee);
        const employeeOffices = getEmployeeOffices(employee);
        const firstCollect = employeeCollects.length > 0 ? employeeCollects[0] : null;
        const firstOffice = employeeOffices.length > 0 ? employeeOffices[0] : null;
        const isUnassigned = isEmployeeUnassigned(employee);

        return {
            id: employee.id, // RowId
            idnumber: employee.idnumber,
            fullName: `${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim(),
            username: employee.username,
            phone: employee.phone,
            cellphone: employee.cellphone,
            office: firstOffice?.name ?? (isUnassigned ? 'Sin oficina' : ''),
            collect: firstCollect?.name ?? (isUnassigned ? 'Sin cobro' : ''),
            isUnassigned,
            mobileSessionStatus:
                Number((employee as any).mobile_session_enabled ?? 1) === 1
                    ? 'Habilitada'
                    : 'Deshabilitada',
        };
    });

    return (
        <HeaderResponsive>
            <StickyListToolbar>
                <Title>Información Empleados</Title>
                <Box sx={{ ml: 'auto' }}>
                    <PermissionButton
                        allowed={canCreate}
                        deniedTooltip="Sin permiso para crear"
                        tooltip="Agregar"
                        variant="contained"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to="/administrar/empleados/add"
                    >
                        {isMobile ? <AddIcon /> : 'Agregar'}
                    </PermissionButton>
                </Box>
            </StickyListToolbar>
            <Card
                sx={{
                    width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'auto',
                }}
            >
                <CardContent sx={{ pb: 0 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="employees-office-filter-label">Seleccionar Oficina</InputLabel>
                                <Select
                                    labelId="employees-office-filter-label"
                                    label="Seleccionar Oficina"
                                    value={officeId}
                                    onChange={(event) => {
                                        const next = String(event.target.value);
                                        setOfficeId(next);
                                        setCollectId('');
                                    }}
                                >
                                    {isSuperAdmin && (
                                        <MenuItem value="">
                                            <em>Todas las oficinas</em>
                                        </MenuItem>
                                    )}
                                    {offices.map((office: Office) => (
                                        <MenuItem key={office.id} value={String(office.id)}>
                                            {office.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={!officeId}>
                                <InputLabel id="employees-collect-filter-label">Seleccionar Cobro</InputLabel>
                                <Select
                                    labelId="employees-collect-filter-label"
                                    label="Seleccionar Cobro"
                                    value={collectId}
                                    onChange={(event) => setCollectId(String(event.target.value))}
                                >
                                    {officeId &&
                                        collectsOffice.map((collect: Collect) => (
                                            <MenuItem key={collect.id} value={String(collect.id)}>
                                                {collect.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {isSuperAdmin && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={showUnassigned}
                                            onChange={(event) => setShowUnassigned(event.target.checked)}
                                        />
                                    }
                                    label="Mostrar sin asignar"
                                />
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
                <CardContent
                    sx={{
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'auto',
                    }}
                >
                    {employeesForTable.length > 0 ? (
                        <SimpleCustomTable
                            rows={rows}
                            columns={simpleColumns}
                            selectionModel={selectedIds}
                            onRowSelectionModelChange={setSelectedIds}
                            maxHeight={600}
                            showBorders
                            showCount
                            wrapCellContent
                            exportCsv
                            exportFileName="empleados.csv"
                            columnGroups={[
                                {
                                    header: 'Opciones',
                                    fields: [
                                        ...(isSuperAdmin ? ['opt_view_password'] : []),
                                        'opt_assign',
                                        'opt_mobile',
                                        'opt_edit',
                                        'opt_delete',
                                    ],
                                },
                            ]}

                        />
                    ) : (
                        <EmptyStateMessage message="No existen registros" align="left" />
                    )}
                </CardContent>
            </Card>
            <Modal open={assignModalOpen} onClose={handleCloseAssignModal}>
                <Paper sx={assignModalStyle}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: 'info.main',
                            color: 'white',
                        }}
                    >
                        <Box>
                            <Typography variant="h6">Asignar cobro</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {assignEmployee
                                    ? `${assignEmployee.first_name} ${assignEmployee.last_name} · ${assignEmployee.username}`
                                    : 'Empleado'}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleCloseAssignModal}
                            sx={{ color: 'white' }}
                            aria-label="cerrar"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        <Stack spacing={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="assign-office-label">Seleccionar oficina</InputLabel>
                                <Select
                                    labelId="assign-office-label"
                                    label="Seleccionar oficina"
                                    value={assignOfficeId}
                                    onChange={(event) => handleAssignOfficeChange(String(event.target.value))}
                                >
                                    {offices.map((office: Office) => (
                                        <MenuItem key={office.id} value={String(office.id)}>
                                            {office.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small" disabled={!assignOfficeId}>
                                <InputLabel id="assign-collect-label">Seleccionar cobro</InputLabel>
                                <Select
                                    labelId="assign-collect-label"
                                    label="Seleccionar cobro"
                                    value={assignCollectId}
                                    onChange={(event) => setAssignCollectId(String(event.target.value))}
                                >
                                    {assignCollects.length === 0 ? (
                                        <MenuItem value="" disabled>
                                            No hay cobros disponibles sin asignar
                                        </MenuItem>
                                    ) : (
                                        assignCollects.map((collect: Collect) => (
                                            <MenuItem key={collect.id} value={String(collect.id)}>
                                                {collect.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>

                            <Typography variant="body2" color="text.secondary">
                                Sólo se muestran los cobros disponibles para tu usuario y que no tienen otro empleado asignado.
                            </Typography>
                        </Stack>
                    </Box>

                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, p: 2 }}>
                        <Button variant="outlined" color="secondary" onClick={handleCloseAssignModal}>
                            Cancelar
                        </Button>
                        <PermissionButton
                            allowed={canAssign}
                            deniedTooltip="Sin permiso para asignar cobro"
                            tooltip="Guardar"
                            variant="contained"
                            color="info"
                            onClick={handleSaveAssignedCollect}
                            disabled={!assignCollectId}
                        >
                            Guardar
                        </PermissionButton>
                    </Box>
                </Paper>
            </Modal>
            <Modal open={mobileModalOpen} onClose={handleCloseMobileModal}>
                <Paper sx={mobileModalStyle}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: 'primary.main',
                            color: 'white',
                        }}
                    >
                        <Box>
                            <Typography variant="h6">Gestión Mobile</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {selectedEmployee
                                    ? `${selectedEmployee.first_name} ${selectedEmployee.last_name} · ${selectedEmployee.username}`
                                    : 'Empleado'}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleCloseMobileModal}
                            sx={{ color: 'white' }}
                            aria-label="cerrar"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Dispositivos Registrados
                        </Typography>
                        {mobileDevices.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                No hay dispositivos registrados para este empleado.
                            </Typography>
                        ) : (
                            <Stack spacing={2} sx={{ mb: 3 }}>
                                {mobileDevices.map((device) => {
                                    const statusColor =
                                        device.status === 'approved'
                                            ? 'success'
                                            : device.status === 'blocked'
                                                ? 'error'
                                                : 'warning';

                                    return (
                                        <Paper key={device.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                                                <Box>
                                                    <Typography variant="subtitle2">
                                                        {device.device_name || 'Dispositivo sin nombre'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        ID instalación: {device.installation_id}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Plataforma: {device.platform || 'N/D'} | Modelo: {device.model || 'N/D'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        App: {device.app_version || 'N/D'} | Último login: {device.last_login_at || 'N/D'}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                                    <Chip
                                                        label={
                                                            device.status === 'approved'
                                                                ? 'Aprobado'
                                                                : device.status === 'blocked'
                                                                    ? 'Bloqueado'
                                                                    : 'Pendiente'
                                                        }
                                                        color={statusColor as any}
                                                        size="small"
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => handleMobileDeviceStatusChange(device.id, 'approved')}
                                                        disabled={device.status === 'approved'}
                                                    >
                                                        Aprobar
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleMobileDeviceStatusChange(device.id, 'blocked')}
                                                        disabled={device.status === 'blocked'}
                                                    >
                                                        Bloquear
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Solicitudes de Dispositivo
                        </Typography>
                        {mobileDeviceRequests.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No hay solicitudes registradas para este empleado.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {mobileDeviceRequests.map((request) => {
                                    const statusColor =
                                        request.status === 'approved'
                                            ? 'success'
                                            : request.status === 'rejected'
                                                ? 'error'
                                                : 'warning';

                                    return (
                                        <Paper key={request.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                                                <Box>
                                                    <Typography variant="subtitle2">
                                                        Solicitud #{request.id}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        ID instalación: {request.installation_id}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Dispositivo: {request.device_name || 'N/D'} | Plataforma: {request.platform || 'N/D'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Motivo: {request.request_reason || 'Sin detalle'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Solicitado: {request.requested_at || 'N/D'}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                                    <Chip
                                                        label={
                                                            request.status === 'approved'
                                                                ? 'Aprobada'
                                                                : request.status === 'rejected'
                                                                    ? 'Rechazada'
                                                                    : 'Pendiente'
                                                        }
                                                        color={statusColor as any}
                                                        size="small"
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => handleMobileDeviceRequestStatusChange(request.id, 'approved')}
                                                        disabled={request.status !== 'pending'}
                                                    >
                                                        Aprobar
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleMobileDeviceRequestStatusChange(request.id, 'rejected')}
                                                        disabled={request.status !== 'pending'}
                                                    >
                                                        Rechazar
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            p: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Button variant="outlined" onClick={handleCloseMobileModal}>
                            Cerrar
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            <Modal open={passwordModalOpen} onClose={handleClosePasswordModal}>
                <Paper sx={passwordModalStyle}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            bgcolor: 'warning.main',
                            color: 'warning.contrastText',
                        }}
                    >
                        <Box>
                            <Typography variant="h6">Contraseña del empleado</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.95 }}>
                                {passwordTarget
                                    ? `${passwordTarget.fullName} · ${passwordTarget.username}`
                                    : ''}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClosePasswordModal}
                            sx={{ color: 'inherit' }}
                            aria-label="cerrar"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            La contraseña guardada está cifrada: no es posible mostrar la clave anterior. Puede
                            generar una contraseña nueva; la anterior dejará de funcionar de inmediato.
                        </Typography>

                        {!generatedPassword ? (
                            <Button
                                variant="contained"
                                color="warning"
                                disabled={passwordLoading}
                                onClick={handleGenerateEmployeePassword}
                            >
                                {passwordLoading ? 'Generando…' : 'Generar nueva contraseña'}
                            </Button>
                        ) : (
                            <Stack spacing={2}>
                                <TextField
                                    label="Nueva contraseña"
                                    value={generatedPassword}
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                    size="small"
                                    sx={{ '& input': { fontFamily: 'monospace', letterSpacing: '0.04em' } }}
                                />
                                <Button variant="outlined" onClick={handleCopyGeneratedPassword}>
                                    Copiar al portapapeles
                                </Button>
                            </Stack>
                        )}
                    </Box>

                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, p: 2 }}>
                        <Button variant="outlined" onClick={handleClosePasswordModal}>
                            Cerrar
                        </Button>
                    </Box>
                </Paper>
            </Modal>
        </HeaderResponsive>
    );
};

export default Empleados;
